import axios from 'axios';
import { athenaBaseUrl, GET } from '../../utils/axios-interceptor';
import { GLOBAL_ASK_AICA_ENDPOINTS } from './constants';

export function getStoredValue(key: string): string {
    if (typeof window === 'undefined') return '';
    return window.sessionStorage?.getItem(key) ?? window.localStorage?.getItem(key) ?? '';
}

/**
 * Auth headers for the global (org-less) Ask AICA surface.
 *
 * INVARIANT: unlike the leads `getAuthHeaders` (askAica/service.ts),
 * this NEVER falls back to a stored org_id. `x-organization-id` is set ONLY when an
 * explicit `orgId` is passed in, and omitted entirely otherwise — so a company-less
 * turn (or a turn before the gate has resolved a company) never leaks the viewer's
 * own/stale org context to Athena.
 */
export function getGlobalAuthHeaders(orgId?: string | null, userId?: string): Record<string, string> {
    const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'x-cerberus-token': getStoredValue('auth_token'),
        'x-user-id': userId ?? getStoredValue('user_id'),
    };
    if (orgId) headers['x-organization-id'] = orgId;
    return headers;
}

/**
 * Dedicated axios instance for the list/history GETs below.
 *
 * INVARIANT: the app-wide `axios` default export carries a mutated
 * `x-organization-id` default header, stamped once by applyInterceptor/addTokenToHeaders
 * (see utils/axios-interceptor.ts) from localStorage and never cleared. In axios 1.6,
 * `axios.create()` MERGES the caller's current defaults into the new instance — it does
 * NOT start from a clean slate — so a stale `x-organization-id` default could otherwise
 * ride along onto this "fresh" instance too. The explicit delete below closes that gap;
 * these calls only ever carry the headers built explicitly per-request in this file.
 */
const globalAskAicaAxios = axios.create();
// addTokenToHeaders (axios-interceptor.ts) stamps this as a direct default header key
// (not under `.common`) — mirror that exact key when clearing it.
delete globalAskAicaAxios.defaults.headers['x-organization-id'];

export function getUserConversationsService(userId?: string) {
    return globalAskAicaAxios({
        method: GET,
        url: `${athenaBaseUrl}${GLOBAL_ASK_AICA_ENDPOINTS.CONVERSATIONS_USER}`,
        headers: getGlobalAuthHeaders(null, userId),
    });
}

/**
 * `orgId` here is the CALLER's own PNO (investorOrgId), not a resolved chat-turn
 * company — this is auth/ownership scoping on a history lookup, not the `/chat`
 * gate turn, so passing it does NOT violate the org-less/two-suppression
 * invariant on `getGlobalAuthHeaders` above. Athena's `/chat/conversation/history`
 * has `org_id` as a genuinely optional query param (Conversations v2, BE-4) — but
 * `investorOrgId` can still be an empty string on a fresh page load (before user
 * data has hydrated), so we still only forward `org_id` (and the matching
 * `x-organization-id` header) when it's a real, non-blank value, keeping an
 * empty-string org indistinguishable from "omitted" rather than sending a
 * blank param.
 */
export function getConversationHistoryService(conversationId: string, userId?: string, orgId?: string | null, pageSize?: number) {
    return globalAskAicaAxios({
        method: GET,
        url: `${athenaBaseUrl}${GLOBAL_ASK_AICA_ENDPOINTS.CONVERSATION_HISTORY}`,
        headers: getGlobalAuthHeaders(orgId, userId),
        params: {
            conversation_id: conversationId,
            user_id: userId,
            ...(orgId ? { org_id: orgId } : {}),
            ...(pageSize ? { page_size: pageSize } : {}),
        },
    });
}

/** Chat stream configuration — consumed by apiHelper's raw-XHR streamer (invariant #1). */
export const GLOBAL_CHAT_CONFIG = {
    URL: `${athenaBaseUrl}${GLOBAL_ASK_AICA_ENDPOINTS.CHAT}`,
    getHeaders: getGlobalAuthHeaders,
};
